// SearchBarAutoCompleterNew.tsx
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  List,
  Spinner,
  Tag,
  TagLabel,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { InfoIcon } from '@chakra-ui/icons'
import { useCombobox } from 'downshift'
import { Base64 } from 'js-base64'
import { isEmpty } from 'lodash'
import { stringify } from 'query-string'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { BsArrowsAngleContract, BsArrowsAngleExpand } from 'react-icons/bs'
import { IoSearch } from 'react-icons/io5'
import { TbPhotoSearch, TbX } from 'react-icons/tb'
import { useHistory } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../../../ui-kit'
import { FeaturesEnum } from '../../../../../utils/features'
import {
  fileTypeOptionsMapping,
  getSearchQueryFromJSON,
  validateFilter,
} from '../../../../../utils/mediaLibrary'
import {
  normalizeSearchUrlForComparison,
  parseAndConvertSearchQueryForAutoCompleter,
  parseSearchQueryWithValidation,
  buildSearchQuery,
} from '../../../../../utils/search'
import { useDebounce } from '../../../../hooks/useDebounce'
import { useDetailLinkBase } from '../../../../hooks/useDetailLinkBase'
import useMediaLibrarySearch from '../../../../hooks/useMediaLibrarySearch'
import type {
  CustomMetadataResponse,
  FileTypeStateObject,
  FormValidationObject,
  ListSavedSearchSuccessResponse,
  MediaLibrarySearchCollectionResponse,
  MediaLibrarySearchFileResponse,
  MediaLibrarySearchFolderResponse,
  MLSort,
  SearchQueryJson,
} from '../../../../interfaces'
import { NavBarContext, UserContext } from '../../../../providers'
import { CustomMetadataContext } from '../../../../providers/CustomMetadataProvider'
import {
  MediaLibraryActions,
  MLContext,
} from '../../../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../../../providers/PublicLinksInfoProvider'
import SaveSearchModalNew from '../../../SavedSearch/SaveSearchModalNew'
import AdvancedSearch from '../../AdvancedSearch'
import { getRecentSearchesDescending, insertRecentSearch } from '../../utils'
import {
  DEFAULT_SEARCH_NAME_AUTOCOMPLETE_LIMIT,
  DEFAULT_SEARCH_NAME_AUTOCOMPLETE_SKIP,
} from '../../../../constants/MediaLibrary'
import { SemanticImageSearch } from '../SemanticImageSearch'
import {
  RenderCollectionItem,
  RenderFileItem,
  RenderFolderItem,
  RenderHardcodedOption,
  RenderNoResults,
  RenderRecentSearches,
  SectionHeader,
} from './Components'

type DSItem =
  | { kind: 'option', label: string, action(): void }
  | {
      kind: 'folder'
      label: string
      folder: MediaLibrarySearchFolderResponse
      action(): void
    }
  | {
      kind: 'file'
      label: string
      file: MediaLibrarySearchFileResponse
      action(): void
    }
  | {
      kind: 'collection'
      label: string
      collection: MediaLibrarySearchCollectionResponse
      action(): void
    }

interface Props {
  isNavigatingToDetail: boolean
  setIsNavigatingToDetail(navigating: boolean): void
  shouldReset: boolean
  setShouldReset(shouldReset: boolean): void
}

// Custom hook for global Enter key handling in builder mode
const useGlobalEnterKey = (
  callback: () => void,
  dependencies: React.DependencyList = [],
) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle Enter key
      if (e.key !== 'Enter') {
        return
      }

      // Don't interfere with form controls
      const target = e.target as Element
      const isFormControl = target.closest(
        'input, select, textarea, [role="combobox"], [role="listbox"], [role="menu"], [role="button"]',
      )

      if (!isFormControl && !e.defaultPrevented) {
        e.preventDefault()
        void callback()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => void document.removeEventListener('keydown', handleKeyDown)
  }, dependencies)
}

const SearchBarAutoCompleterNew: React.FC<Props> = ({
  isNavigatingToDetail,
  setIsNavigatingToDetail,
  shouldReset,
  setShouldReset,
}) => {
  const { cmFields: originalCustomMetadataFields } = useContext(
    CustomMetadataContext,
  )

  const customMetadataFields = originalCustomMetadataFields.map(x => ({
    ...x,
    name: `"customMetadata.${x.name}"`,
  }))

  const customMetadataFieldsMapping: {
    [fieldName: string]: CustomMetadataResponse
  } = useMemo(() => {
    const ret: {
      [fieldName: string]: CustomMetadataResponse
    } = {}
    customMetadataFields.forEach(customMetadataField => {
      ret[customMetadataField.name] = customMetadataField
    })
    return ret
  }, [customMetadataFields])
  /* ************************* CUSTOM METADATA FIELDS FETCH END ********************* */

  const { t } = useTranslation()

  // --- outside click + Esc to close ---
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Save search modal state (moved up)
  const {
    isOpen: isSaveSearchModalOpen,
    onOpen: onOpenSaveSearchModal,
    onClose: onCloseSaveSearchModal,
  } = useDisclosure()

  const { background } = useDetailLinkBase()

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const node = containerRef.current
      if (!node) {
        return
      }

      // Don't close if ANY modal is open OR navigating to detail
      if (isSaveSearchModalOpen || isNavigatingToDetail) {
        return
      }

      const target = e.target as Node

      // Check if click is inside AddFilterMenu or its submenus using class names
      const clickedElement = target as HTMLElement
      const isInsideAddFilterMenu =
        clickedElement.closest(
          '.add-filter-menu-main, .add-filter-menu-embedded, .add-filter-menu-custom',
        ) !== null

      if (!node.contains(target) && !isInsideAddFilterMenu) {
        // tell parent (index.tsx) to close; no props needed
        window.dispatchEvent(new CustomEvent('mlSearchAutocomplete:close'))
      }
    }

    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Don't close autocomplete if ANY modal is open OR navigating to detail (let modal handle ESC)
        if (isSaveSearchModalOpen || isNavigatingToDetail) {
          return
        }
        window.dispatchEvent(new CustomEvent('mlSearchAutocomplete:close'))
      }
    }

    // capture so this runs before other handlers that might stopPropagation
    document.addEventListener('mousedown', onDocMouseDown, true)
    window.addEventListener('keydown', onEsc)

    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true)
      window.removeEventListener('keydown', onEsc)
    }
  }, [isSaveSearchModalOpen, isNavigatingToDetail]) // Add all dependencies

  const toast = useToast()
  const history = useHistory()

  useEffect(() => {
    const unlisten = history.listen(location => {
      const isDetail = /\/media-library\/detail\/[^/]+/.test(location.pathname)
      if (!isDetail && isNavigatingToDetail) {
        setIsNavigatingToDetail(false)
      }
    })
    return unlisten
  }, [history, isNavigatingToDetail])

  // global state
  const [mlState, dispatch] = useContext(MLContext)
  const { user } = useContext(UserContext)
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const {
    savedSearches: contextSavedSearches,
    setSavedSearches: setContextSavedSearches,
  } = useContext(NavBarContext)

  // ---- local mirrors of MLProvider (init once) ----
  const [searchQuery, setSearchQuery] = useState<string>(
    mlState.searchQuery || '',
  )
  const [searchQueryJson, setSearchQueryJson] = useState<SearchQueryJson>(
    mlState.searchQueryJson,
  )
  const [searchInputMode, setSearchInputMode] = useState<string>(
    mlState.searchInputMode,
  )
  const [searchMode, setSearchMode] = useState<string | null>(
    mlState.searchMode,
  ) // 'keyword' | 'ai-text' | 'ai-image'
  const [q, setQ] = useState<string>(mlState.q || '')
  const [queryText, setQueryText] = useState<string>(mlState.searchQuery || '')
  const [isVersionIncludedInSearch, setIsVersionIncludedInSearch] =
    useState<boolean>(mlState.isVersionIncludedInSearch ?? false)
  const [searchByTags, setSearchByTags] = useState<boolean>(
    mlState.searchByTags ?? false,
  )
  const [fileType, setFileType] = useState<FileTypeStateObject>({
    text: !isEmpty(mlState.fileType)
      ? t(fileTypeOptionsMapping[mlState.fileType])
      : '',
    value: mlState.fileType,
  })

  // Saved search state variables (kept for modal functionality)
  const [isLoadingSavedSearches, setIsLoadingSavedSearches] =
    useState<boolean>(false)

  const [isQueryTextareaExpanded, setIsQueryTextareaExpanded] =
    useState<boolean>(false)
  const [isManuallyCollapsed, setIsManuallyCollapsed] = useState<boolean>(false)
  const [isManuallyExpanded, setIsManuallyExpanded] = useState<boolean>(false)

  const localRef = React.useRef<HTMLInputElement>(null)
  const maxWidthRef = useRef<HTMLDivElement>(null)
  const inputRightElementRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (localRef.current) {
      localRef.current.focus()
    }
  }, [])

  // Calculate dynamic padding based on right element width
  useEffect(() => {
    if (inputRightElementRef.current && localRef.current) {
      const width = inputRightElementRef.current.offsetWidth
      // Set the CSS custom property directly on the input element
      localRef.current.style.setProperty('--right-width', `${width + 8}px`)
    }
  }, [q, searchMode]) // Recalculate when right element content changes


  const disableBuilderMode = useMemo(() => {
    let _disabledBuilderMode = false
    if (searchInputMode === 'query' && !isEmpty(queryText)) {
      try {
        // Just check if parsing succeeds - we don't need the result here
        parseAndConvertSearchQueryForAutoCompleter(
          queryText,
          originalCustomMetadataFields,
        )
      } catch (error) {
        _disabledBuilderMode = true
      }
    }
    return _disabledBuilderMode
  }, [searchInputMode, queryText, originalCustomMetadataFields])

  const debouncedSearchQuery = useDebounce(searchQuery, 200)
  const debouncedSearchByTags = useDebounce(searchByTags, 200)
  const debouncedIsVersionIncludedInSearch = useDebounce(
    isVersionIncludedInSearch,
    200,
  )
  const debouncedFileType = useDebounce(fileType, 200)

  const validationObject: FormValidationObject = useMemo(() => {
    let isValid = true
    const errors: Array<Array<string | null>> = []

    searchQueryJson.filterGroups.forEach((filterGroup, groupIndex) => {
      errors.push([])
      filterGroup.expressions.forEach((expression, filterIndex) => {
        const validation = validateFilter({
          expression,
          customMetadataField:
            customMetadataFieldsMapping[expression.fieldName],
        })
        if (!validation.isValid) {
          isValid = false
        }
        errors[groupIndex][filterIndex] = validation.error
      })
    })

    return {
      isValid,
      errors,
    }
  }, [searchQueryJson, customMetadataFieldsMapping])

  useEffect(() => {
    setSearchQueryJson(mlState.searchQueryJson)
  }, [mlState.searchQueryJson])

  useEffect(() => {
    setSearchQuery(mlState.searchQuery)
  }, [mlState.searchQuery])

  useEffect(() => {
    setSearchInputMode(mlState.searchInputMode)
  }, [mlState.searchInputMode])

  useEffect(() => {
    setSearchMode(mlState.searchMode)
  }, [mlState.searchMode])

  useEffect(() => {
    setFileType({
      text: !isEmpty(mlState.fileType)
        ? t(fileTypeOptionsMapping[mlState.fileType])
        : '',
      value: mlState.fileType,
    })
  }, [mlState.fileType])

  useEffect(() => {
    setQ(mlState.q)
  }, [mlState.q])

  useEffect(() => {
    setSearchByTags(mlState.searchByTags ?? false)
  }, [mlState.searchByTags])

  useEffect(() => {
    setIsVersionIncludedInSearch(mlState.isVersionIncludedInSearch ?? false)
  }, [mlState.isVersionIncludedInSearch])

  useEffect(() => {
    if (validationObject.isValid) {
      const newSearchQuery = getSearchQueryFromJSON({
        searchQueryJson,
        customMetadataFieldsMapping,
      })
      setSearchQuery(newSearchQuery)
    }
  }, [searchQueryJson, customMetadataFieldsMapping, validationObject])

  // Auto-expand textarea when content exceeds single line
  useEffect(() => {
    if (searchInputMode !== 'query' || !textareaRef.current) {
      return
    }

    // Use requestAnimationFrame to ensure DOM has updated before measuring
    const rafId = requestAnimationFrame(() => {
      const textarea = textareaRef.current
      if (!textarea) {
        return
      }

      const singleLineHeight = 36 // Single line height in pixels
      const scrollHeight = textarea.scrollHeight

      // If content exceeds single line and not manually collapsed
      if (scrollHeight > singleLineHeight) {
        if (!isManuallyCollapsed) {
          setIsQueryTextareaExpanded(true)
          setIsManuallyExpanded(false) // Reset manual expand flag when auto-expanding
        }
      } else {
        // Content fits in single line - auto-collapse unless manually expanded
        if (!isManuallyExpanded) {
          setIsQueryTextareaExpanded(false)
          setIsManuallyCollapsed(false) // Reset manual collapse flag when auto-collapsing
        }
      }
    })

    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [queryText, searchInputMode, isManuallyCollapsed, isManuallyExpanded])

  // When switching to query mode, populate input with the actual search query
  const handleSearchInputModeChange = useCallback(
    (mode: 'builder' | 'query') => {
      if (mode === searchInputMode) {
        return
      }
      setSearchInputMode(mode)
      // Reset manual override flags when switching modes
      setIsManuallyCollapsed(false)
      setIsManuallyExpanded(false)
      if (mode === 'query') {
        const finalQueryText = buildSearchQuery({
          assetType: null,
          isVersionIncludedInSearch: false, // since assetType is null, it will not be included in the search query
          q,
          searchMode,
          searchInputMode: 'builder',
          searchByTags,
          searchQuery, // Pass existing searchQuery - buildSearchQuery will combine it
          fileType: fileType.value,
        })
        setQueryText(finalQueryText)
        // reset everything else except isVersionIncludedInSearch
        setFileType({
          text: '',
          value: '',
        })
        setSearchByTags(false)
        setSearchMode(null)
        setSearchQueryJson({
          boolean: 'and',
          filterGroups: [],
        })
        setQ('')
        setSearchQuery('')
      } else {
        // Switching from query mode to builder mode
        if (isEmpty(queryText)) {
          setSearchQueryJson({
            boolean: 'and',
            filterGroups: [],
          })
          setQ('')
          setSearchQuery('')
          setSearchMode('keyword')
          setSearchByTags(true)
          setFileType({
            text: '',
            value: '',
          })
        }
        try {
          const parsedResult = parseAndConvertSearchQueryForAutoCompleter(
            queryText,
            originalCustomMetadataFields,
          )
          // The function returns { searchQueryJson, q, fileType, searchQuery }
          setSearchQueryJson(parsedResult.searchQueryJson)
          setQ(parsedResult.q || '')
          setSearchQuery(parsedResult.searchQuery || '')
          setSearchMode('keyword')
          if (parsedResult.q && parsedResult.q.trim().length > 0) {
            setSearchByTags(true)
          } else {
            setSearchByTags(false)
          }
          // Extract fileType from parsed result
          if (
            parsedResult.fileType &&
            parsedResult.fileType.trim().length > 0
          ) {
            setFileType({
              text: t(fileTypeOptionsMapping[parsedResult.fileType]),
              value: parsedResult.fileType,
            })
          } else {
            setFileType({
              text: '',
              value: '',
            })
          }
        } catch (error) {
          // This should never happen as we disable builder mode if the query is invalid
          console.error('Failed to parse query for builder mode:', error)
        }
      }
    },
    [
      q,
      searchMode,
      searchByTags,
      fileType,
      queryText,
      originalCustomMetadataFields,
      searchQuery,
      t,
      searchInputMode,
    ],
  )
  const [queryError, setQueryError] = useState<string>('')

  // Generate URL for saving search (replicate pushToResults logic)
  const urlToSave: string = useMemo(() => {
    const query: Record<string, string> = {}

    // Only include parameters if they have meaningful values
    if (!searchByTags) {
      query.searchByTags = String(searchByTags)
    }

    if (isVersionIncludedInSearch) {
      query.isVersionIncludedInSearch = String(isVersionIncludedInSearch)
    }

    if (q.trim().length > 0 && searchMode && searchInputMode !== 'query') {
      query.searchMode = searchMode
    }

    if (searchInputMode && searchInputMode !== 'builder') {
      query.searchInputMode = searchInputMode
    }

    // Only include q if it has a value
    if (q && q.trim()) {
      query.q = q
    }

    // Only include searchQuery if it has a value
    if (searchQuery && searchQuery.trim()) {
      query.searchQuery = searchQuery
    }

    if (fileType.value.trim().length > 0) {
      query.fileType = fileType.value
    }

    // For query mode, include the query text
    if (searchInputMode === 'query' && queryText && queryText.trim()) {
      query.searchQuery = queryText
    }

    const qs = stringify(query, { skipNull: true })
    // return full url
    return `${window.location.origin}/search?${qs}`
  }, [
    searchByTags,
    isVersionIncludedInSearch,
    searchMode,
    searchInputMode,
    q,
    searchQuery,
    queryText,
    fileType,
  ])

  // Check if the current search is already saved
  const isSearchAlreadySaved = useMemo(() => {
    if (!contextSavedSearches || contextSavedSearches.length === 0) {
      return false
    }

    const normalizedCurrentUrl = normalizeSearchUrlForComparison(
      urlToSave,
      originalCustomMetadataFields,
    )

    if (!normalizedCurrentUrl) {
      return false
    }

    return contextSavedSearches.some(savedSearch => {
      const normalizedSavedUrl = normalizeSearchUrlForComparison(
        savedSearch.url,
        originalCustomMetadataFields,
      )
      return normalizedSavedUrl === normalizedCurrentUrl
    })
  }, [
    urlToSave,
    contextSavedSearches,
    normalizeSearchUrlForComparison,
    originalCustomMetadataFields,
  ])

  // Disabled states for save search buttons
  const isBuilderSaveDisabled = useMemo(
    () =>
      (searchQuery.trim().length === 0 &&
        !q &&
        fileType.value.trim().length === 0 &&
        !isVersionIncludedInSearch) ||
      !validationObject.isValid ||
      isSearchAlreadySaved,
    [
      searchQuery,
      q,
      fileType.value,
      isVersionIncludedInSearch,
      validationObject.isValid,
      isSearchAlreadySaved,
    ],
  )

  const isQuerySaveDisabled = useMemo(() => {
    if (!queryText.trim().length) {
      return true
    }
    if (isSearchAlreadySaved) {
      return true
    }
    try {
      const parsedQuery = parseSearchQueryWithValidation(
        queryText,
        originalCustomMetadataFields,
      )
      if (parsedQuery === null) {
        throw new Error('Invalid query')
      }
      return false
    } catch (error: any) {
      console.error('Error parsing search query:', error)
      return true
    }
  }, [queryText, originalCustomMetadataFields, isSearchAlreadySaved])

  // Tooltip messages for save search buttons
  const builderSaveTooltipMessage = useMemo(() => {
    if (isSearchAlreadySaved) {
      return 'Saved search is already present'
    }
    if (!validationObject.isValid) {
      return 'Invalid search - please fix validation errors'
    }
    if (
      searchQuery.trim().length === 0 &&
      !q &&
      fileType.value.trim().length === 0 &&
      !isVersionIncludedInSearch
    ) {
      return 'Please enter a search query'
    }
    return ''
  }, [
    isSearchAlreadySaved,
    validationObject.isValid,
    searchQuery,
    q,
    fileType.value,
    isVersionIncludedInSearch,
  ])

  const querySaveTooltipMessage = useMemo(() => {
    if (isSearchAlreadySaved) {
      return 'Saved search is already present'
    }
    if (!queryText.trim().length) {
      return 'Please enter a search query'
    }
    try {
      const parsedQuery = parseSearchQueryWithValidation(
        queryText,
        originalCustomMetadataFields,
      )
      if (parsedQuery === null) {
        throw new Error('Invalid query')
      }
      return ''
    } catch (error: any) {
      return 'Invalid query'
    }
  }, [isSearchAlreadySaved, queryText, originalCustomMetadataFields])

  // Handle save success callback
  const handleSaveSuccess = () => {
    onCloseSaveSearchModal()
    // Trigger saved searches refresh if needed
    void getAndSetSavedSearches()
  }

  const isValidSearchQuery = useCallback(
    (query: string) => {
      try {
        const parsedQuery = parseSearchQueryWithValidation(
          query,
          originalCustomMetadataFields,
        )
        if (parsedQuery === null) {
          throw new Error('Invalid query')
        }
        setQueryError('') // Clear error on success
        return true
      } catch (error: any) {
        setQueryError('Invalid query')
        return false
      }
    },
    [originalCustomMetadataFields],
  )

  // results
  const [files, setFiles] = useState<MediaLibrarySearchFileResponse[]>([])
  const [folders, setFolders] = useState<MediaLibrarySearchFolderResponse[]>([])
  const [collections, setCollections] = useState<
    MediaLibrarySearchCollectionResponse[]
  >([])

  const [loadingAny, setLoadingAny] = useState<boolean>(false)
  const [hadError, setHadError] = useState<boolean>(false)

  // recent + saved searches (inline)
  const recentSearches = useMemo(() => getRecentSearchesDescending(false), [])
  const { request } = useFetch({ cachePolicy: CachePolicies.NO_CACHE })

  // debounce for networked lists (reduced for better responsiveness)
  const debouncedQ = useDebounce(q, 200)

  // show SemanticImageSearch screen?
  const showSemanticImage = Boolean(mlState.openSemanticImageSearch)

  // a shim so SemanticImageSearch can "close modal" without parent props
  const setSearchFocusedShim = (val: boolean) => {
    if (!val) {
      window.dispatchEvent(new CustomEvent('mlSearchAutocomplete:close'))
    }
  }

  // guards
  const immediateHasQ = q.trim().length > 0 // for 3 options (instant)
  const hasSearchQuery =
    Boolean(mlState.searchQuery) && mlState.searchQuery.trim().length > 0
  const suppressAll = useMemo(
    () =>
      (debouncedQ.trim().length === 0 &&
        debouncedSearchQuery.trim().length === 0 &&
        debouncedFileType.value.trim().length === 0 &&
        !debouncedIsVersionIncludedInSearch) ||
      showSemanticImage ||
      searchInputMode === 'query' ||
      !validationObject.isValid,
    [
      debouncedQ,
      debouncedSearchQuery,
      hasSearchQuery,
      showSemanticImage,
      searchInputMode,
      validationObject,
      debouncedFileType,
      debouncedIsVersionIncludedInSearch,
    ],
  )

  const suppressFolderRequest = useMemo(
    () =>
      suppressAll ||
      debouncedIsVersionIncludedInSearch ||
      searchMode === 'ai-text' ||
      searchMode === 'ai-image',
    [suppressAll, debouncedIsVersionIncludedInSearch, searchMode],
  )

  const suppressCollectionRequest = useMemo(
    () =>
      suppressAll ||
      debouncedIsVersionIncludedInSearch ||
      !user?.pricingPlan.featuresList.includes(
        FeaturesEnum.ACL_AND_COLLECTIONS,
      ) ||
      searchMode === 'ai-text' ||
      searchMode === 'ai-image',
    [
      suppressAll,
      debouncedIsVersionIncludedInSearch,
      user?.pricingPlan.featuresList,
      searchMode,
    ],
  )

  // core params
  const sort: MLSort = 'DESC_RELEVANCE'
  const limit = DEFAULT_SEARCH_NAME_AUTOCOMPLETE_LIMIT
  const skip = DEFAULT_SEARCH_NAME_AUTOCOMPLETE_SKIP

  // fetchers (global: path=null) - using debounced values
  const filesResp = useMediaLibrarySearch<MediaLibrarySearchFileResponse>({
    path: null,
    skip,
    limit,
    sort,
    q: debouncedQ,
    searchMode,
    searchInputMode,
    searchByTags: debouncedSearchByTags,
    fileType: debouncedFileType.value,
    semanticSupported: true,
    suppressRequest: suppressAll,
    assetType: 'file',
    isVersionIncludedInSearch: debouncedIsVersionIncludedInSearch,
    searchQuery: debouncedSearchQuery,
    onData: res => {
      // if (!res.success) {
      //   setHadError(true)
      // }
      setFiles(res.items || [])
    },
    onLoading: loading => void setLoadingAny(prev => loading || prev),
  })

  const foldersResp = useMediaLibrarySearch<MediaLibrarySearchFolderResponse>({
    path: null,
    skip,
    limit,
    sort,
    q: debouncedQ,
    searchMode,
    searchInputMode,
    searchByTags: debouncedSearchByTags,
    suppressRequest: suppressFolderRequest,
    fileType: debouncedFileType.value,
    assetType: 'folder',
    isVersionIncludedInSearch: false,
    searchQuery: debouncedSearchQuery,
    onData: res => {
      // if (!res.success) {
      //   setHadError(true)
      // }
      setFolders(res.items || [])
    },
    onLoading: loading => void setLoadingAny(prev => loading || prev),
  })

  const collectionsResp =
    useMediaLibrarySearch<MediaLibrarySearchCollectionResponse>({
      path: null,
      collectionId: true,
      skip,
      limit,
      sort,
      q: debouncedQ,
      searchMode,
      searchInputMode,
      searchByTags: debouncedSearchByTags,
      fileType: debouncedFileType.value,
      suppressRequest: suppressCollectionRequest,
      assetType: 'media-collection',
      isVersionIncludedInSearch: false,
      searchQuery: debouncedSearchQuery ?? '',
      onData: res => {
        // if (!res.success) {
        //   setHadError(true)
        // }
        setCollections(res.items || [])
      },
      onLoading: loading => void setLoadingAny(prev => loading || prev),
    })

  // Saved search API integration
  const getAndSetSavedSearches = useCallback(async () => {
    if (!user?.clientNumber) {
      return
    }

    setIsLoadingSavedSearches(true)
    try {
      const res = (await request.get(
        `/api/media-library/${user.clientNumber}/saved-searches`,
      )) as ListSavedSearchSuccessResponse[]

      if (request.error) {
        toast({
          description:
            'Error in getting saved searches, please try again later.',
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else if (Array.isArray(res)) {
        setContextSavedSearches(res)
      }
    } catch (error) {
      console.error('Error fetching saved searches:', error)
    } finally {
      setIsLoadingSavedSearches(false)
    }
  }, [user?.clientNumber, request, toast, setContextSavedSearches])

  useEffect(() => {
    setLoadingAny(
      filesResp.loading || foldersResp.loading || collectionsResp.loading,
    )
  }, [filesResp.loading, foldersResp.loading, collectionsResp.loading])

  useEffect(() => {
    if (suppressAll) {
      setFiles([])
      setFolders([])
      setCollections([])
    }
  }, [suppressAll])

  useEffect(() => {
    if (suppressFolderRequest) {
      setFolders([])
    }
  }, [suppressFolderRequest])

  useEffect(() => {
    if (suppressCollectionRequest) {
      setCollections([])
    }
  }, [suppressCollectionRequest])

  // results-page navigation
  const pushToResults = useCallback(
    (params: {
      searchMode: string | null
      q: string
      searchQuery: string
      searchByTags: boolean
      isVersionIncludedInSearch: boolean
      fileType: FileTypeStateObject
      searchInputMode: string
    }) => {
      const query: Record<string, string> = {}

      // Only include parameters if they have meaningful values
      if (!params.searchByTags) {
        query.searchByTags = String(params.searchByTags)
      }

      if (params.isVersionIncludedInSearch) {
        query.isVersionIncludedInSearch = String(
          params.isVersionIncludedInSearch,
        )
      }

      // Include searchMode unless searchInputMode is 'query'
      if (
        params.q &&
        params.q.trim().length > 0 &&
        params.searchMode &&
        params.searchInputMode !== 'query'
      ) {
        query.searchMode = params.searchMode
      }

      if (
        params.searchInputMode &&
        params.searchInputMode !== 'builder' &&
        params.searchQuery.trim().length > 0
      ) {
        query.searchInputMode = params.searchInputMode
      }

      if (params.fileType.value) {
        query.fileType = params.fileType.value
      }

      // Only include q if it has a value
      if (params.q && params.q.trim()) {
        query.q = params.q
      }

      // Only include searchQuery if it has a value
      if (params.searchQuery && params.searchQuery.trim()) {
        query.searchQuery = params.searchQuery
      }

      query.sort = 'DESC_RELEVANCE'

      const qs = stringify(query, { skipNull: true })
      history.push({
        pathname: '/search',
        search: qs,
      })
      // Close modal after navigation
      window.dispatchEvent(new CustomEvent('mlSearchAutocomplete:close'))
    },
    [history],
  )

  const onSubmit = useCallback(
    (typedQ?: string) => {
      const qVal = (typedQ ?? q).trim()
      if (searchInputMode === 'builder' && !validationObject.isValid) {
        return
      }

      const finalParams: {
        searchMode: string | null
        q: string
        searchQuery: string
        searchByTags: boolean
        isVersionIncludedInSearch: boolean
        fileType: FileTypeStateObject
        searchInputMode: string
      } = {
        searchMode,
        q: qVal,
        searchQuery,
        searchByTags,
        isVersionIncludedInSearch,
        fileType,
        searchInputMode,
      }

      if (searchInputMode === 'query') {
        // User modified the query - use queryText as searchQuery, set searchMode to null
        finalParams.searchQuery = queryText
        finalParams.q = ''
        finalParams.searchMode = null
        finalParams.searchByTags = false
        finalParams.isVersionIncludedInSearch = false
        finalParams.fileType = {
          text: '',
          value: '',
        }
        setSearchMode(null)
      }

      // only insert recent search if user is typing in builder mode and there is no search query
      // and there is no version included in search and no file type selected
      if (
        searchInputMode !== 'query' &&
        searchMode !== 'ai-text' &&
        searchMode !== 'ai-image' &&
        qVal.trim().length > 0 &&
        searchQuery.trim().length === 0 &&
        !isVersionIncludedInSearch &&
        fileType.value.trim().length === 0
      ) {
        insertRecentSearch(qVal, false)
      }
      pushToResults({
        searchMode: finalParams.searchMode,
        q: finalParams.q,
        searchQuery: finalParams.searchQuery,
        searchByTags: finalParams.searchByTags,
        isVersionIncludedInSearch: finalParams.isVersionIncludedInSearch,
        fileType: finalParams.fileType,
        searchInputMode: finalParams.searchInputMode,
      })
    },
    [
      q,
      searchMode,
      searchInputMode,
      queryText,
      searchQuery,
      pushToResults,
      validationObject,
      isVersionIncludedInSearch,
      fileType,
      searchByTags,
    ],
  )

  // Global Enter key handling for builder mode
  useGlobalEnterKey(() => {
    if (
      searchInputMode === 'builder' &&
      (q.trim().length > 0 ||
        searchQuery.trim().length > 0 ||
        fileType.value.trim().length > 0 ||
        isVersionIncludedInSearch)
    ) {
      onSubmit()
    }
  }, [
    searchInputMode,
    q,
    searchQuery,
    fileType.value,
    isVersionIncludedInSearch,
    onSubmit,
  ])

  const onAiText = useCallback(
    (typedQ?: string) => {
      const val = (typedQ ?? q).trim()
      if (!val) {
        return
      }
      pushToResults({
        searchMode: 'ai-text',
        q: val,
        searchQuery: '',
        searchByTags: false,
        isVersionIncludedInSearch: false,
        fileType: {
          text: '',
          value: '',
        },
        searchInputMode: 'builder',
      })
      setSearchMode('ai-text')
    },
    [
      q,
      pushToResults,
      searchByTags,
      isVersionIncludedInSearch,
      fileType,
      searchInputMode,
    ],
  )

  // Reset search function - resets all search-related state
  const resetSearch = useCallback(() => {
    setQ('')
    setSearchQuery('')
    setSearchQueryJson({
      boolean: 'and',
      filterGroups: [],
    })
    setSearchByTags(true)
    setIsVersionIncludedInSearch(false)
    setSearchMode('keyword')
    setQueryText('')
    setFileType({ text: '', value: '' })
    setQueryError('')
    // Reset manual override flags
    setIsManuallyCollapsed(false)
    setIsManuallyExpanded(false)
    setIsQueryTextareaExpanded(false)
  }, [])

  // open handlers
  const openFolder = (folderPath: string) => {
    history.push({
      pathname: `/media-library/${Base64.encode(folderPath, true)}`,
    })
    // Close modal after navigation
    window.dispatchEvent(new CustomEvent('mlSearchAutocomplete:close'))
  }
  const openFile = (fileId: string) => {
    setIsNavigatingToDetail(true) // Use prop setter

    // Disable navigation when opening file from search autocompleter
    dispatch({
      type: MediaLibraryActions.SET_DISABLE_DETAIL_NAVIGATION,
      payload: true,
    })

    void history.push({
      pathname: `/media-library/detail/${fileId}`,
      state: { background },
    })
  }
  const openCollection = (collectionId: string) => {
    history.push({
      pathname: `/media-collections/${collectionId}`,
    })
    // Close modal after navigation
    window.dispatchEvent(new CustomEvent('mlSearchAutocomplete:close'))
  }

  // toast on any error
  useEffect(() => {
    if (hadError) {
      toast({
        description: 'Error in fetching data. Try refreshing the page.',
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      setHadError(false)
    }
  }, [hadError, toast])

  const showCollections =
    !isPublicLink &&
    user?.pricingPlan.featuresList.includes(FeaturesEnum.ACL_AND_COLLECTIONS)

  // The hooks will automatically refetch when debouncedQ changes
  // No need for manual refetch calls - this improves performance

  const noInput = useMemo(
    () =>
      debouncedQ.trim().length === 0 &&
      debouncedSearchQuery.trim().length === 0 &&
      debouncedFileType.value.trim().length === 0 &&
      !debouncedIsVersionIncludedInSearch,
    [
      debouncedQ,
      debouncedSearchQuery,
      debouncedFileType.value,
      debouncedIsVersionIncludedInSearch,
    ],
  )

  const showRecent = useMemo(
    () => noInput || searchInputMode === 'query',
    [noInput, searchInputMode],
  )

  const showImmediateOptions = useMemo(
    () =>
      immediateHasQ && searchMode !== 'ai-text' && searchMode !== 'ai-image',
    [immediateHasQ, searchMode],
  )

  const emptyLists = useMemo(
    () =>
      folders.length === 0 &&
      files.length === 0 &&
      (!showCollections || collections.length === 0),
    [folders.length, files.length, showCollections, collections.length],
  )

  const showNoResult = useMemo(
    () =>
      searchInputMode === 'builder' &&
      !loadingAny &&
      !noInput &&
      (emptyLists || !validationObject.isValid),
    [
      searchInputMode,
      loadingAny,
      noInput,
      emptyLists,
      validationObject.isValid,
    ],
  )

  const showResultsSections = useMemo(
    () => !showRecent && !loadingAny && searchInputMode === 'builder',
    [showRecent, loadingAny, searchInputMode],
  )

  const showOptionsSection = useMemo(
    () => showImmediateOptions && searchInputMode === 'builder',
    [showImmediateOptions, searchInputMode],
  )

  // InputLeftElement for AI modes (similar to index.tsx file type tags)
  const inputLeftElement = useMemo<React.ReactElement>(() => {
    if (searchMode === 'ai-text') {
      return (
        <Tag padding="0.3em" marginLeft="0.5em" bg="#0450d5">
          <TagLabel textColor="white">AI POWERED</TagLabel>
        </Tag>
      )
    }

    // AI Image mode - show magnifying glass + image preview + Searching by image tag
    if (searchMode === 'ai-image') {
      return (
        <Tag
          padding="0.3em"
          marginLeft="0.5em"
          bg={mlState.fileListError ? 'appVeryLightPink' : '#0450d5'}
        >
          {mlState.fileListError ? (
            <Icon
              as={InfoIcon}
              color="appWatermelon"
              boxSize="14px"
              mr="0.5em"
            />
          ) : (
            q && (
              <Box
                h="16px"
                w="16px"
                backgroundImage={`url(${mlState.q})`}
                backgroundPosition="center"
                backgroundSize="cover"
                backgroundRepeat="no-repeat"
                borderRadius="2px"
                mr="0.5em"
                border="1px solid #fff"
              />
            )
          )}
          <TagLabel
            textColor={mlState.fileListError ? 'appWatermelon' : 'white'}
          >
            {t('searching_by_image', 'Searching by image')}
          </TagLabel>
        </Tag>
      )
    }
    return null
  }, [searchMode, q, mlState.fileListError, t])

  // ---------- Downshift items composition ----------
  const dsItems: DSItem[] = useMemo(() => {
    const items: DSItem[] = []

    // Immediate options (no debounce) – always from *q*
    if (showImmediateOptions) {
      items.push(
        {
          kind: 'option',
          label: `Search all results for "${q}"`,
          action: () => {
            void onSubmit(q)
          },
        },
        {
          kind: 'option',
          label: `Search for images related to "${q}"`,
          action: () => {
            void onAiText(q)
          },
        },
      )
    }

    // Debounced sections
    folders.forEach(f =>
      items.push({
        kind: 'folder',
        label: f.name,
        folder: f,
        action: () => {
          void openFolder(f.folderPath)
        },
      }),
    )

    files.forEach(file =>
      items.push({
        kind: 'file',
        label: file.name,
        file,
        action: () => void openFile(file.fileId),
      }),
    )

    if (showCollections) {
      collections.forEach(c =>
        items.push({
          kind: 'collection',
          label: c.name,
          collection: c,
          action: () => {
            void openCollection(c.mediaCollectionId)
          },
        }),
      )
    }

    return items
  }, [
    q,
    showImmediateOptions,
    debouncedQ,
    showRecent,
    recentSearches,
    contextSavedSearches,
    folders,
    files,
    collections,
    showCollections,
    onSubmit,
    onAiText,
    history,
    searchMode,
  ])

  // stateReducer to mimic legacy Enter behavior
  const stateReducer = useCallback(
    (state, actionAndChanges) => {
      const { type, changes } = actionAndChanges
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter: {
          if (state.highlightedIndex === -1) {
            if (q.trim().length > 0) {
              onSubmit(q)
            }
            return changes
          }
          return changes
        }
        case useCombobox.stateChangeTypes.InputBlur:
          return { ...changes, isOpen: false }
        default:
          return changes
      }
    },
    [q, onSubmit],
  )

  const {
    getInputProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectItem,
  } = useCombobox<DSItem>({
    items: dsItems,
    inputValue: searchMode === 'ai-image' ? '' : q,
    onInputValueChange: ({ inputValue }) => {
      if (typeof inputValue === 'string') {
        setQ(inputValue)
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) {
        return
      }
      selectedItem.action()
    },
    itemToString: () => debouncedQ,
    stateReducer,
    // Disable combobox when semantic image search is shown
    isOpen: showSemanticImage ? false : undefined,
  })

  // ---------- RENDER ----------
  const portalContent = ReactDOM.createPortal(
    <Box
      ref={containerRef}
      position="fixed"
      zIndex={1389}
      top="80px"
      left="50%"
      transform="translateX(-50%)"
    >
      <VStack
        spacing="0"
        width="70vw"
        maxHeight="70vh"
        bgColor={colors.appWhite}
        borderRadius="0.25rem"
        border="1px solid #e6e7ea"
        alignItems="flex-start"
        overflow="hidden"
        paddingTop="12px"
        paddingBottom="12px"
      >
        {showSemanticImage ? (
          <>
            {/* Hidden elements to satisfy downshift requirements */}
            <Input
              {...getInputProps()}
              position="absolute"
              opacity={0}
              pointerEvents="none"
              width={0}
              height={0}
              margin={0}
              padding={0}
              border="none"
              aria-hidden="true"
              tabIndex={-1}
            />
            <Box
              {...getMenuProps()}
              position="absolute"
              opacity={0}
              pointerEvents="none"
              width={0}
              height={0}
              margin={0}
              padding={0}
              aria-hidden="true"
            />
            <VStack
              spacing="0"
              gap="12px"
              px="4"
              w="full"
              paddingTop="6px"
              paddingBottom="24px"
              flex="1"
              minHeight={0}
              overflowY="auto"
            >
              <SemanticImageSearch
                setSearchFocused={setSearchFocusedShim}
                shouldReset={shouldReset}
              />
            </VStack>
          </>
        ) : (
          <>
            {/* Header: Tabs, Search Input, and Action Links */}
            <Box w="full" px="16px" pb="8px">
              {/* Main search row: Tabs are alongside the input */}
              <HStack
                spacing="4"
                align={isQueryTextareaExpanded ? 'flex-start' : 'center'}
              >
                <HStack
                  spacing="2"
                  borderRadius="4px"
                  border="solid 1px #e6e7ea"
                  padding="4px"
                >
                  <Tooltip
                    label={
                      disableBuilderMode
                        ? t(
                            'clear_query_to_revert_to_builder_mode',
                            'Clear query to revert to builder mode',
                          )
                        : ''
                    }
                    hasArrow
                    shouldWrapChildren
                  >
                    <Button
                      h="26px"
                      px="12px"
                      py="4px"
                      borderRadius="3px"
                      fontSize="14px"
                      fontWeight="normal"
                      // --- Conditional styles based on Zeplin spec ---
                      bg={searchInputMode === 'builder' ? '#eff6ff' : 'white'}
                      color={
                        searchInputMode === 'builder' ? '#0450d5' : '#3b485e'
                      }
                      onClick={() => {
                        void handleSearchInputModeChange('builder')
                      }}
                      isDisabled={disableBuilderMode}
                    >
                      Builder
                    </Button>
                  </Tooltip>
                  <Tooltip
                    label={
                      searchMode === 'ai-text'
                        ? t(
                            'clear_ai_text_search_to_enable_query_mode',
                            'Clear AI text search to enable query mode',
                          )
                        : searchMode === 'ai-image'
                        ? t(
                            'clear_ai_image_search_to_enable_query_mode',
                            'Clear AI image search to enable query mode',
                          )
                        : ''
                    }
                    hasArrow
                    shouldWrapChildren
                  >
                    <Button
                      h="26px"
                      px="12px"
                      py="4px"
                      borderRadius="3px"
                      fontSize="14px"
                      fontWeight="normal"
                      bg={searchInputMode === 'query' ? '#eff6ff' : 'white'}
                      color={
                        searchInputMode === 'query' ? '#0450d5' : '#3b485e'
                      }
                      onClick={() => {
                        void handleSearchInputModeChange('query')
                      }}
                      isDisabled={Boolean(
                        searchMode === 'ai-text' || searchMode === 'ai-image',
                      )}
                    >
                      Query
                    </Button>
                  </Tooltip>
                </HStack>

                {/* Search Input Area (takes remaining space) */}
                <Box flex="1">
                  {searchInputMode === 'query' ? (
                    <Box
                      position="relative"
                      w="full"
                      height={isQueryTextareaExpanded ? '160px' : '36px'}
                    >
                      <Textarea
                        ref={textareaRef}
                        value={queryText}
                        borderRadius="0.25rem"
                        onChange={e => {
                          setQueryText(e.target.value)
                          // Clear error when user starts typing
                          if (queryError) {
                            setQueryError('')
                          }
                        }}
                        placeholder="Type your advanced query…"
                        minHeight={isQueryTextareaExpanded ? '160px' : '36px'}
                        resize="none"
                        borderColor={queryError ? 'red.300' : '#e6e7ea'}
                        focusBorderColor={
                          queryError ? 'red.500' : 'appBlue.500'
                        }
                        _hover={{
                          borderColor: queryError ? 'red.300' : '#e6e7ea',
                        }}
                        pr="80px" // Padding to prevent text from overlapping icons
                        onKeyDown={e => {
                          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                            e.preventDefault()
                            // check if queryText is a valid search query
                            if (isValidSearchQuery(queryText)) {
                              void onSubmit()
                            }
                          }
                        }}
                      />
                      <HStack
                        position="absolute"
                        top="0"
                        right="3"
                        spacing="1"
                        zIndex="1"
                      >
                        <Tooltip
                          label={
                            isQueryTextareaExpanded ? 'Collapse' : 'Expand'
                          }
                          hasArrow
                        >
                          <IconButton
                            aria-label={
                              isQueryTextareaExpanded
                                ? 'Collapse textarea'
                                : 'Expand textarea'
                            }
                            icon={
                              isQueryTextareaExpanded ? (
                                <BsArrowsAngleContract />
                              ) : (
                                <BsArrowsAngleExpand />
                              )
                            }
                            variant="outline"
                            border="none"
                            borderRadius="0"
                            colorScheme="#a0aec0"
                            fontSize="1.25em"
                            onClick={() => {
                              const newExpandedState = !isQueryTextareaExpanded
                              setIsQueryTextareaExpanded(newExpandedState)

                              // Set manual override flags
                              if (newExpandedState) {
                                setIsManuallyExpanded(true)
                                setIsManuallyCollapsed(false)
                              } else {
                                setIsManuallyCollapsed(true)
                                setIsManuallyExpanded(false)
                              }
                            }}
                          />
                        </Tooltip>
                        <Tooltip label="Search (Ctrl+Enter)" hasArrow>
                          <IconButton
                            aria-label="Run search query"
                            icon={<IoSearch />}
                            variant="outline"
                            border="none"
                            borderRadius="0"
                            colorScheme="#a0aec0"
                            fontSize="1.25em"
                            isDisabled={!queryText.trim()}
                            onClick={() => {
                              if (isValidSearchQuery(queryText)) {
                                void onSubmit()
                              }
                            }}
                          />
                        </Tooltip>
                      </HStack>

                      {/* Error message display */}
                      {queryError && (
                        <Text
                          color="red.500"
                          fontSize="sm"
                          mt="1"
                          ml="1"
                          fontWeight="500"
                          justifyContent="flex-end"
                          width="full"
                          display="flex"
                        >
                          {queryError}
                        </Text>
                      )}
                    </Box>
                  ) : (
                    <HStack spacing="4" flex="1">
                      <InputGroup>
                        {inputLeftElement && (
                          <InputLeftElement
                            children={inputLeftElement}
                            paddingLeft="0.5em"
                            position="static"
                            width="unset"
                            borderWidth="1px"
                            borderColor="#e6e7ea"
                            borderRadius="0.25rem"
                            borderRightWidth="0"
                            borderRightRadius="0"
                            cursor={
                              searchMode === 'ai-text' ||
                              searchMode === 'ai-image'
                                ? 'default'
                                : 'pointer'
                            }
                          />
                        )}
                        <Input
                          placeholder={
                            searchMode === 'ai-image'
                              ? ''
                              : 'Start typing to search'
                          }
                          height="36px"
                          fontWeight="normal"
                          borderLeftWidth={inputLeftElement ? '0' : '1px'}
                          borderLeftRadius={inputLeftElement ? '0' : '0.25rem'}
                          focusBorderColor="appBlue.500"
                          _hover={{ borderColor: '#e6e7ea' }}
                          isReadOnly={
                            searchMode === 'ai-text' ||
                            searchMode === 'ai-image'
                          }
                          paddingLeft={inputLeftElement ? '0.5em' : 'auto'}
                          {...getInputProps({
                            ref: localRef,
                            onKeyDown: e => {
                              if (
                                e.key === 'Enter' &&
                                highlightedIndex === -1
                              ) {
                                e.preventDefault()
                                void onSubmit()
                              }
                            },
                          })}
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          paddingRight="var(--right-width, 0.5em)"
                        />
                        <InputRightElement
                          ref={inputRightElementRef}
                          width="unset"
                        >
                          <HStack spacing="8px">
                            {/* Clear */}
                            <IconButton
                              aria-label="Clear"
                              icon={<TbX />}
                              variant="outline"
                              border="none"
                              borderRadius="0"
                              colorScheme="#a0aec0"
                              fontSize="1.25em"
                              onClick={() => {
                                if (q) {
                                  setQ('')
                                  selectItem(null)
                                }
                                if (
                                  searchMode === 'ai-text' ||
                                  searchMode === 'ai-image'
                                ) {
                                  setSearchMode('keyword')
                                }
                              }}
                            />
                            {user?.pricingPlan.featuresList.includes(
                              FeaturesEnum.SEMANTIC_SEARCH,
                            ) && (
                              <>
                                {/* Vertical divider */}
                                <Divider orientation="vertical" height="18px" />
                                {/* Search by image */}
                                <IconButton
                                  aria-label="Search by image"
                                  icon={<TbPhotoSearch />}
                                  variant="outline"
                                  border="none"
                                  borderRadius="0"
                                  colorScheme="#a0aec0"
                                  fontSize="1.25em"
                                  onClick={() => {
                                    setShouldReset(true)
                                    dispatch({
                                      type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
                                      payload: true,
                                    })
                                  }}
                                />
                              </>
                            )}
                          </HStack>
                        </InputRightElement>
                      </InputGroup>
                      <HStack spacing="4">
                        <Button
                          variant="link"
                          fontSize="14px"
                          colorScheme="gray"
                          fontWeight="500"
                          onClick={resetSearch}
                        >
                          Reset search
                        </Button>
                        <Tooltip
                          label={builderSaveTooltipMessage}
                          isDisabled={
                            !isBuilderSaveDisabled || !builderSaveTooltipMessage
                          }
                          hasArrow
                          shouldWrapChildren
                        >
                          <Button
                            variant="link"
                            fontSize="14px"
                            colorScheme="appBlue"
                            fontWeight="500"
                            isDisabled={isBuilderSaveDisabled}
                            onClick={() => void onOpenSaveSearchModal()}
                          >
                            Save search
                          </Button>
                        </Tooltip>
                      </HStack>
                    </HStack>
                  )}
                </Box>

                {/* ACTION LINKS: Render alongside input ONLY in Query mode */}
                {searchInputMode === 'query' && (
                  <HStack
                    spacing="4"
                    paddingTop={isQueryTextareaExpanded ? '7.5px' : '0px'}
                  >
                    <Button
                      variant="link"
                      fontSize="14px"
                      colorScheme="gray"
                      fontWeight="500"
                      onClick={resetSearch}
                    >
                      Reset search
                    </Button>
                    <Tooltip
                      label={querySaveTooltipMessage}
                      isDisabled={
                        !isQuerySaveDisabled || !querySaveTooltipMessage
                      }
                      hasArrow
                      shouldWrapChildren
                    >
                      <Button
                        variant="link"
                        fontSize="14px"
                        colorScheme="appBlue"
                        fontWeight="500"
                        isDisabled={isQuerySaveDisabled}
                        onClick={() => void onOpenSaveSearchModal()}
                      >
                        Save search
                      </Button>
                    </Tooltip>
                  </HStack>
                )}
              </HStack>

              {/* ACTION LINKS: Render BELOW input in Builder and Query modes */}
              <HStack spacing="4" mt="3" ref={maxWidthRef}>
                <AdvancedSearch
                  searchByTags={searchByTags}
                  setSearchByTags={setSearchByTags}
                  isVersionIncludedInSearch={isVersionIncludedInSearch}
                  setIsVersionIncludedInSearch={setIsVersionIncludedInSearch}
                  searchQueryJson={searchQueryJson}
                  setSearchQueryJson={setSearchQueryJson}
                  customMetadataFields={customMetadataFields}
                  customMetadataFieldsMapping={customMetadataFieldsMapping}
                  validationObject={validationObject}
                  exposeVersionSettingOnly={searchInputMode === 'query'}
                  fileType={fileType}
                  setFileType={setFileType}
                  maxWidthRef={maxWidthRef}
                />
              </HStack>
            </Box>

            {/* Menu (Downshift) – debounced sections */}
            <VStack width="full" alignItems="start" spacing={0}>
              <List
                width="full"
                maxH="50vh"
                overflowY="auto"
                overflowX="hidden"
                {...getMenuProps()}
              >
                {/* OPTIONS: show instantly (no debounce) - only in builder mode */}
                {showOptionsSection && (
                  <>
                    <Divider />
                    {dsItems.map((item, idx) => {
                      if (!item) {
                        return null
                      }

                      // Render hardcoded options (Search all results, Search for images related)
                      if (item.kind === 'option') {
                        return (
                          <RenderHardcodedOption
                            key={`option-${idx}`}
                            item={item}
                            index={idx}
                            highlightedIndex={highlightedIndex}
                            itemProps={getItemProps({ item, index: idx })}
                          />
                        )
                      }

                      // Render real search results (files, folders, collections, recent, saved)
                      return null // These will be rendered in their respective sections below
                    })}
                  </>
                )}

                {/* Loading */}
                {loadingAny && (
                  <>
                    <Divider />
                    <Flex w="full" justify="center" py="8">
                      <Spinner
                        thickness="2px"
                        speed="0.65s"
                        emptyColor="appPaleGrey"
                        color="appBlue"
                        size="md"
                      />
                    </Flex>
                  </>
                )}

                {/* q present (debounced): sections - only in builder mode */}
                {showResultsSections && (
                  <>
                    {/* Folders */}
                    {(() => {
                      // Base index: 2 when immediate options are present; else 0
                      const base = showImmediateOptions ? 2 : 0
                      if (folders.length === 0) {
                        return null
                      }
                      return (
                        <>
                          <Divider />
                          <SectionHeader>Folders</SectionHeader>
                          {folders.map((f, i) => {
                            const idx = base + i
                            const item = dsItems[idx]
                            return (
                              <RenderFolderItem
                                key={`folder-${f.folderId}`}
                                folder={f}
                                index={idx}
                                highlightedIndex={highlightedIndex}
                                debouncedQ={debouncedQ}
                                itemProps={getItemProps({ item, index: idx })}
                                searchMode={searchMode}
                              />
                            )
                          })}
                        </>
                      )
                    })()}

                    {/* Files */}
                    {(() => {
                      // Base index: 2 when immediate options are present; else 0
                      const base =
                        (showImmediateOptions ? 2 : 0) + folders.length
                      if (files.length === 0) {
                        return null
                      }
                      return (
                        <>
                          <Divider />
                          <SectionHeader>Files</SectionHeader>
                          {files.map((file, i) => {
                            const idx = base + i
                            const item = dsItems[idx]
                            return (
                              <RenderFileItem
                                key={`file-${file.fileId}-${
                                  file.versionInfo.id ?? 'v0'
                                }`}
                                file={file}
                                index={idx}
                                highlightedIndex={highlightedIndex}
                                debouncedQ={debouncedQ}
                                itemProps={getItemProps({ item, index: idx })}
                                searchByTags={searchByTags}
                                searchMode={searchMode}
                              />
                            )
                          })}
                        </>
                      )
                    })()}

                    {/* Collections */}
                    {(() => {
                      if (!showCollections) {
                        return null
                      }
                      // Base index: 2 when immediate options are present; else 0
                      const base =
                        (showImmediateOptions ? 2 : 0) +
                        folders.length +
                        files.length
                      if (collections.length === 0) {
                        return null
                      }
                      return (
                        <>
                          <Divider />
                          <SectionHeader>Collections</SectionHeader>
                          {collections.map((c, i) => {
                            const idx = base + i
                            const item = dsItems[idx]
                            return (
                              <RenderCollectionItem
                                key={`collection-${c.mediaCollectionId}`}
                                collection={c}
                                index={idx}
                                highlightedIndex={highlightedIndex}
                                debouncedQ={debouncedQ}
                                itemProps={getItemProps({ item, index: idx })}
                                searchMode={searchMode}
                              />
                            )
                          })}
                        </>
                      )
                    })()}
                  </>
                )}

                {/* No results */}
                {showNoResult && (
                  <>
                    <Divider />
                    <RenderNoResults />
                  </>
                )}
              </List>
            </VStack>
            {/* Query mode: show recent searches when query text is empty */}
            {showRecent && !loadingAny && (
              <RenderRecentSearches
                recentSearches={recentSearches}
                pushToResults={pushToResults}
              />
            )}
          </>
        )}
      </VStack>
      <SaveSearchModalNew
        isOpen={isSaveSearchModalOpen}
        onClose={onCloseSaveSearchModal}
        url={urlToSave}
        onSaveSuccess={handleSaveSuccess}
      />
    </Box>,
    document.body,
  )

  return portalContent
}

export default SearchBarAutoCompleterNew
